import type {SketchDimensionKind, SketchDimensionSnapshot} from '../SketchDimensionTypes';
import type {SketchDimensionLayoutVec2, SketchDimensionReferenceAnchor} from './SketchDimensionLayoutResolver';

/**
 * Instant2D 手柄值反算（阶段 7 基础版，纯函数）。
 *
 * 设计说明：
 * 1. 与 `SketchDimensionLayoutResolver` 同一套锚点语义：拖动点按尺寸核心类型
 *    反算"手柄位置对应的新业务值"（nominalValue 语义，直径已 ×2）；
 * 2. 角度双分支（θ 与 π-θ）按当前值择近，保证拖动连续不跳变；
 * 3. 特殊类型（doubled/symmetric/arc-length/spline-length）不支持手柄——
 *    其值与拖动点的映射依赖中心线/弧分支语义，留实机对照阶段再定。
 */

/** 支持 Instant2D 手柄的核心类型集合。 */
const SUPPORTED_KINDS: ReadonlySet<SketchDimensionKind> = new Set([
    'point-distance',
    'length',
    'point-line-distance',
    'line-line-distance',
    'angle',
    'three-point-angle',
    'radius',
    'diameter',
]);

/** 向量工具（与布局解析器同语义）。 */
const subtract = (a: SketchDimensionLayoutVec2, b: SketchDimensionLayoutVec2): SketchDimensionLayoutVec2 => [a[0] - b[0], a[1] - b[1]];
const dot = (a: SketchDimensionLayoutVec2, b: SketchDimensionLayoutVec2): number => a[0] * b[0] + a[1] * b[1];
const norm = (a: SketchDimensionLayoutVec2): number => Math.hypot(a[0], a[1]);
const normalize = (a: SketchDimensionLayoutVec2): SketchDimensionLayoutVec2 | null => {
    const length = norm(a);

    return length < 1e-12 ? null : [a[0] / length, a[1] / length];
};

/** 读取点语义锚点（点锚点取自身，圆/椭圆取中心，抛物线取顶点，线锚点取起点，折线取首点）。 */
function anchorPoint(anchor: SketchDimensionReferenceAnchor | undefined): SketchDimensionLayoutVec2 | null {
    if (!anchor) {
        return null;
    }
    if (anchor.kind === 'point') {
        return anchor.position;
    }
    if (anchor.kind === 'circle') {
        return anchor.center;
    }
    if (anchor.kind === 'ellipse') {
        return anchor.center;
    }
    if (anchor.kind === 'parabola') {
        return anchor.apex;
    }
    // 折线锚点（样条）取首点：总长类尺寸本就不支持手柄反算，这里只保证类型完备。
    if (anchor.kind === 'polyline') {
        return anchor.points[0] ?? null;
    }

    return anchor.start;
}

/** 读取线锚点。 */
function lineAnchor(
    anchors: readonly (SketchDimensionReferenceAnchor | undefined)[],
    index: number,
): Extract<SketchDimensionReferenceAnchor, {kind: 'line'}> | null {
    const anchor = anchors[index];

    return anchor?.kind === 'line' ? anchor : null;
}

/** 点到无限直线的垂距。 */
function distanceToLine(
    point: SketchDimensionLayoutVec2,
    line: Extract<SketchDimensionReferenceAnchor, {kind: 'line'}>,
): number | null {
    const direction = normalize(subtract(line.end, line.start));

    if (!direction) {
        return null;
    }
    const offset = subtract(point, line.start);
    const along = dot(offset, direction);

    return Math.hypot(offset[0] - direction[0] * along, offset[1] - direction[1] * along);
}

/**
 * 角度分支择近（取靠近当前值的那一支，保证拖动连续）。
 *
 * 两线夹角只有 θ 与 π−θ 两支（四条射线切出的扇区必 ≤π）；三点角是两条真实射线，
 * 还存在优角支 2π−θ 与 π+θ——圆弧槽口常见跨角超过平角，缺这两支会在拖动时被压回劣角。
 *
 * @param direction 基准射线单位方向。
 * @param toPoint 顶点指向拖动点的向量。
 * @param currentValue 当前名义值（弧度）。
 * @param allowReflex 是否允许优角支。
 * @returns 新角度值；方向退化返回 null。
 */
function resolveAngleNearCurrent(
    direction: SketchDimensionLayoutVec2,
    toPoint: SketchDimensionLayoutVec2,
    currentValue: number,
    allowReflex = false,
): number | null {
    const pointDirection = normalize(toPoint);

    if (!pointDirection) {
        return null;
    }
    const primary = Math.acos(Math.min(Math.max(dot(direction, pointDirection), -1), 1));
    const candidates = allowReflex
        ? [primary, Math.PI - primary, Math.PI * 2 - primary, Math.PI + primary]
        : [primary, Math.PI - primary];

    return candidates.reduce((best, candidate) => (
        Math.abs(candidate - currentValue) < Math.abs(best - currentValue) ? candidate : best
    ));
}

/**
 * 判断核心类型是否支持 Instant2D 手柄。
 *
 * @param kind 尺寸核心类型。
 * @returns 是否支持。
 */
export function isSketchDimensionInstant2dSupported(kind: SketchDimensionKind): boolean {
    return SUPPORTED_KINDS.has(kind);
}

/**
 * 按拖动点反算尺寸新业务值。
 *
 * @param snapshot 尺寸快照（分支/当前值用于消歧）。
 * @param anchors 按引用顺序解析的几何锚点。
 * @param point 拖动点（草图局部坐标，米）。
 * @returns 新业务值（核心单位，>0）；几何退化或不支持时返回 null。
 */
export function resolveSketchDimensionInstant2dValue(
    snapshot: SketchDimensionSnapshot,
    anchors: readonly (SketchDimensionReferenceAnchor | undefined)[],
    point: SketchDimensionLayoutVec2,
): number | null {
    let value: number | null = null;

    switch (snapshot.dimensionKind) {
        case 'point-distance': {
            const first = anchorPoint(anchors[0]);
            const second = anchorPoint(anchors[1]);

            if (!first || !second) {
                return null;
            }
            const orientation = snapshot.branch.orientation ?? 'aligned';

            if (orientation === 'horizontal') {
                value = Math.abs(point[0] - first[0]);
            } else if (orientation === 'vertical') {
                value = Math.abs(point[1] - first[1]);
            } else {
                const direction = normalize(subtract(second, first));

                value = direction ? Math.abs(dot(subtract(point, first), direction)) : null;
            }
            break;
        }
        case 'length': {
            const line = lineAnchor(anchors, 0);

            if (!line) {
                return null;
            }

            /** H/V 固定方向语义（阶段 8）：反算随 branch.orientation 投影。 */
            const lengthOrientation = snapshot.branch.orientation ?? 'aligned';

            if (lengthOrientation === 'horizontal') {
                value = Math.abs(point[0] - line.start[0]);
            } else if (lengthOrientation === 'vertical') {
                value = Math.abs(point[1] - line.start[1]);
            } else {
                const direction = normalize(subtract(line.end, line.start));

                value = direction ? Math.abs(dot(subtract(point, line.start), direction)) : null;
            }
            break;
        }
        case 'point-line-distance':
        case 'line-line-distance': {
            const lineIndex = snapshot.dimensionKind === 'line-line-distance'
                ? 0
                : (anchors[0]?.kind === 'line' ? 0 : 1);
            const line = lineAnchor(anchors, lineIndex);

            value = line ? distanceToLine(point, line) : null;
            break;
        }
        case 'angle': {
            const first = lineAnchor(anchors, 0);
            const second = lineAnchor(anchors, 1);
            const firstDirection = first ? normalize(subtract(first.end, first.start)) : null;
            const secondDirection = second ? normalize(subtract(second.end, second.start)) : null;

            if (!first || !second || !firstDirection || !secondDirection) {
                return null;
            }
            const denominator = firstDirection[0] * secondDirection[1] - firstDirection[1] * secondDirection[0];

            if (Math.abs(denominator) < 1e-12) {
                return null;
            }
            const t = ((second.start[0] - first.start[0]) * secondDirection[1]
                - (second.start[1] - first.start[1]) * secondDirection[0]) / denominator;
            const vertex: SketchDimensionLayoutVec2 = [
                first.start[0] + firstDirection[0] * t,
                first.start[1] + firstDirection[1] * t,
            ];

            value = resolveAngleNearCurrent(firstDirection, subtract(point, vertex), snapshot.parameter.nominalValue);
            break;
        }
        case 'three-point-angle': {
            const vertex = anchorPoint(anchors[0]);
            const firstRay = anchorPoint(anchors[1]);
            const firstDirection = vertex && firstRay ? normalize(subtract(firstRay, vertex)) : null;

            value = vertex && firstDirection
                ? resolveAngleNearCurrent(
                    firstDirection,
                    subtract(point, vertex),
                    snapshot.parameter.nominalValue,
                    true,
                )
                : null;
            break;
        }
        case 'radius':
        case 'diameter': {
            const circle = anchors[0]?.kind === 'circle' ? anchors[0] : null;

            if (!circle) {
                return null;
            }
            const distance = norm(subtract(point, circle.center));

            value = snapshot.dimensionKind === 'diameter' ? distance * 2 : distance;
            break;
        }
        default:
            return null;
    }

    return value !== null && Number.isFinite(value) && value > 1e-9 ? value : null;
}
